import React, { useEffect, useRef, useState } from "react";
import { Briefcase, TrendingUp, PieChart, LineChart, Users2 } from "lucide-react";

const profiles = [
  {
    icon: Briefcase,
    title: "Investment Advisors & Wealth Consultants",
    description:
      "Professionals providing investment advice and wealth management services to clients.",
  },
  {
    icon: TrendingUp,
    title: "Private Equity & Venture Consultants",
    description:
      "Specialists in alternative investments and private market opportunities.",
  },
  {
    icon: PieChart,
    title: "Mutual Fund Distributors",
    description:
      "Authorized distributors of mutual funds and investment products.",
  },
  {
    icon: LineChart,
    title: "Stock Brokers & Capital Market Intermediaries",
    description:
      "Licensed professionals facilitating capital market transactions.",
  },
  {
    icon: Users2,
    title: "Professionals Working with HNIs, UHNIs & Family Offices",
    description:
      "Advisors serving high-net-worth individuals and institutional clients.",
  },
];

export default function WhoShouldAttend() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayInterval = useRef<NodeJS.Timeout>();

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;

    autoPlayInterval.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % profiles.length);
    }, 4000); // Change slide every 4 seconds

    return () => {
      if (autoPlayInterval.current) {
        clearInterval(autoPlayInterval.current);
      }
    };
  }, [isAutoPlaying]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Manual navigation
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    if (autoPlayInterval.current) {
      clearInterval(autoPlayInterval.current);
    }
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000); // Resume after 5 seconds
  };

  const goToPrev = () => {
    goToSlide(currentIndex === 0 ? profiles.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    goToSlide(currentIndex === profiles.length - 1 ? 0 : currentIndex + 1);
  };

  return (
    <section className="relative py-16 md:py-24 px-4 sm:px-6 md:px-8 lg:px-12 bg-white overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#D4AF37]/5 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-[#0E6F7E]/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Heading */}
        <div className="text-center mb-12 md:mb-16 px-4">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-0.5 w-8 bg-gradient-to-r from-transparent to-[#D4AF37]"></div>
            <span className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase">
              TARGET AUDIENCE
            </span>
            <div className="h-0.5 w-8 bg-gradient-to-l from-transparent to-[#D4AF37]"></div>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#07162E] mb-4 md:mb-6">
            Who Should Attend
          </h2>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            This program is designed for capital market professionals and
            investment experts who are committed to elevating their practice to
            institutional standards.
          </p>
        </div>

        {/* 🔹 MOBILE CAROUSEL (sm and below) */}
        <div className="lg:hidden relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="relative overflow-hidden rounded-3xl">
            {/* Carousel Container */}
            <div 
              ref={carouselRef}
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {profiles.map((profile, index) => (
                <div 
                  key={index} 
                  className="w-full flex-shrink-0 px-2"
                >
                  <div className="group bg-white rounded-2xl p-6 sm:p-8
                    shadow-[0_8px_30px_rgba(2,8,23,0.08)]
                    hover:shadow-[0_20px_50px_rgba(2,8,23,0.15)]
                    transition-all duration-300 transform hover:-translate-y-2
                    border border-gray-100 hover:border-[#0E6F7E]/30
                    h-full"
                  >
                    <div className="flex flex-col items-center text-center">
                      {/* Icon */}
                      <div
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl
                        bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E]
                        flex items-center justify-center mb-4 sm:mb-6
                        group-hover:scale-110 transition-transform duration-300
                        shadow-lg"
                      >
                        <profile.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#1DBF73]" />
                      </div>

                      {/* Content */}
                      <h3 className="text-lg sm:text-xl font-bold text-[#07162E] mb-3">
                        {profile.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {profile.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Buttons */}
            {/* <button
              onClick={goToPrev}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm
                shadow-lg border border-gray-200 flex items-center justify-center
                text-gray-700 hover:text-[#0E6F7E] transition-all duration-300
                hover:scale-110 hover:shadow-xl active:scale-95"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button> */}

            {/* <button
              onClick={goToNext}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm
                shadow-lg border border-gray-200 flex items-center justify-center
                text-gray-700 hover:text-[#0E6F7E] transition-all duration-300
                hover:scale-110 hover:shadow-xl active:scale-95"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
            </button> */}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center items-center gap-2 mt-6">
            {profiles.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? "bg-gradient-to-r from-[#0E6F7E] to-[#1DBF73] w-3 h-3 sm:w-4 sm:h-4"
                    : "bg-gray-300 hover:bg-gray-400 w-2 h-2 sm:w-3 sm:h-3"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* 🔹 DESKTOP/TABLET GRID (lg and above) */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {profiles.slice(0, 3).map((profile, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 lg:p-8
                  shadow-[0_8px_30px_rgba(2,8,23,0.08)]
                  hover:shadow-[0_20px_50px_rgba(2,8,23,0.15)]
                  transition-all duration-300 transform hover:-translate-y-2
                  border border-gray-100 hover:border-[#0E6F7E]/30
                  h-full"
              >
                <div className="flex flex-col items-center text-center h-full">
                  {/* Icon */}
                  <div
                    className="w-16 h-16 lg:w-20 lg:h-20 rounded-xl
                    bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E]
                    flex items-center justify-center mb-6
                    group-hover:scale-110 transition-transform duration-300
                    shadow-lg"
                  >
                    <profile.icon className="w-8 h-8 lg:w-10 lg:h-10 text-[#1DBF73]" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl lg:text-2xl font-bold text-[#07162E] mb-4">
                    {profile.title}
                  </h3>
                  <p className="text-base lg:text-lg text-gray-600 leading-relaxed flex-grow">
                    {profile.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom two cards for desktop */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {profiles.slice(3).map((profile, index) => (
              <div
                key={index + 3}
                className="group bg-white rounded-2xl p-8
                  shadow-[0_8px_30px_rgba(2,8,23,0.08)]
                  hover:shadow-[0_20px_50px_rgba(2,8,23,0.15)]
                  transition-all duration-300 transform hover:-translate-y-2
                  border border-gray-100 hover:border-[#0E6F7E]/30
                  h-full"
              >
                <div className="flex flex-col items-center text-center h-full">
                  {/* Icon */}
                  <div
                    className="w-20 h-20 rounded-xl
                    bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E]
                    flex items-center justify-center mb-6
                    group-hover:scale-110 transition-transform duration-300
                    shadow-lg"
                  >
                    <profile.icon className="w-10 h-10 text-[#1DBF73]" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-[#07162E] mb-4">
                    {profile.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed flex-grow">
                    {profile.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-100">
            <div className="text-3xl sm:text-4xl font-bold text-[#0E6F7E] mb-2">1000+</div>
            <div className="text-gray-600 font-medium">Professionals Trained</div>
          </div>
          <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-100">
            <div className="text-3xl sm:text-4xl font-bold text-[#0E6F7E] mb-2">25+</div>
            <div className="text-gray-600 font-medium">Years Combined Experience</div>
          </div>
          <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-100">
            <div className="text-3xl sm:text-4xl font-bold text-[#0E6F7E] mb-2">50+</div>
            <div className="text-gray-600 font-medium">Industry Experts</div>
          </div>
        </div>
      </div>
    </section>
  );
}